import {
  actorMayCreateOrDeleteDirection,
  actorMayModifyDirection,
  actorMayModifyEquipe,
  actorMayMutateSections,
  type OrgEditActor,
} from './orgEditAffordances'

export type EditScopeNotice = {
  title: string
  description: string
}

const LIMITED_TITLE = 'Mode édition limité'

function trimmedNames(values: string[]): string[] {
  return values.map((entry) => entry.trim()).filter((entry) => entry.length > 0)
}

function sameOrg(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase()
}

/** Vue nationale : l’admin ne modifie que ses directions, sans création ni suppression. */
export function nationalEditScopeNotice(actor: OrgEditActor): EditScopeNotice | null {
  if (actorMayCreateOrDeleteDirection(actor)) {
    return null
  }
  const names = trimmedNames(actor.directionAdminDirLibs).filter((name) =>
    actorMayModifyDirection(actor, name),
  )
  if (names.length === 0) {
    return null
  }
  const label = names.join(', ')
  return {
    title: LIMITED_TITLE,
    description: `Le contour marque votre direction (${label}). Vous pouvez la modifier, pas la supprimer, ni en créer une autre.`,
  }
}

/**
 * Page direction. Super-admin et développeur : pas de toast.
 * Admin de la direction : peut modifier l’arbre, pas supprimer la direction.
 * Manageur de direction : toute la page est modifiable, sans contour ; le toast rappelle
 * qu’il ne modifie pas la direction elle-même.
 * Manageur de domaine : le contour ne marque que sa colonne.
 */
export function directionPageEditScopeNotice(
  actor: OrgEditActor,
  dirLib: string,
): EditScopeNotice | null {
  if (actorMayCreateOrDeleteDirection(actor)) {
    return null
  }
  const direction = dirLib.trim()
  if (direction.length === 0) {
    return null
  }
  if (actorMayModifyDirection(actor, direction)) {
    return {
      title: LIMITED_TITLE,
      description: `Vous pouvez modifier l’organigramme de ${direction}. La suppression de la direction reste impossible.`,
    }
  }
  const leadsThisDirection = (actor.managerScope?.directionLeaders ?? []).some((entry) =>
    sameOrg(entry.dirLib, direction),
  )
  if (leadsThisDirection) {
    return {
      title: LIMITED_TITLE,
      description: `Vous pouvez modifier les domaines de ${direction}, ainsi que leurs agences, équipes et sections. La direction elle-même ne se modifie pas.`,
    }
  }
  const domains = trimmedNames(
    (actor.managerScope?.domainLeaders ?? [])
      .filter((entry) => sameOrg(entry.dirLib, direction))
      .map((entry) => entry.domLib),
  )
  if (domains.length === 0) {
    return null
  }
  return {
    title: LIMITED_TITLE,
    description: `Le contour marque votre domaine (${domains.join(', ')}). Vous pouvez y ajouter des agences et modifier les équipes. Les autres colonnes restent en consultation.`,
  }
}

/** Fiche équipe : l’ancrage équipe ne modifie pas l’équipe, seulement ses sections et agents. */
export function equipePageEditScopeNotice(
  actor: OrgEditActor,
  place: { dirLib: string; domLib: string; ageLib: string; equLib: string },
): EditScopeNotice | null {
  const mayModifyEquipe = actorMayModifyEquipe(actor, place.dirLib, place.domLib, place.ageLib)
  const mayMutateSections = actorMayMutateSections(
    actor,
    place.dirLib,
    place.domLib,
    place.ageLib,
    place.equLib,
  )
  if (mayModifyEquipe || !mayMutateSections) {
    return null
  }
  const team = place.equLib.trim() || 'cette équipe'
  return {
    title: LIMITED_TITLE,
    description: `Le contour marque les sections de ${team}. Vous pouvez y ajouter des agents et gérer les sections. L’équipe elle-même ne se modifie pas.`,
  }
}
